import { api } from './api';
import { storage } from './storage';
import type { LoginResponse, User } from '@/types/api';

export async function login(email: string, password: string): Promise<LoginResponse> {
  const { data } = await api.post<LoginResponse>('/api/v1/auth/login', { email, password });
  storage.setTokens(data.access_token, data.refresh_token);
  storage.setUser(data.user);
  return data;
}

export async function logout(): Promise<void> {
  const refresh = storage.getRefresh();
  try {
    if (refresh) {
      await api.post('/api/v1/auth/logout', { refresh_token: refresh });
    }
  } catch {
    // Logout is best-effort; always clear local state.
  } finally {
    storage.clear();
  }
}

export async function fetchMe(): Promise<User> {
  const { data } = await api.get<User>('/api/v1/auth/me');
  return data;
}

export interface MessageResponse {
  status: 'ok';
  message: string;
}

/**
 * Requests a password-reset email. The response message is identical
 * whether the email matches a real account or not — that's a deliberate
 * defense against account enumeration and should be surfaced to the
 * user verbatim.
 */
export async function forgotPassword(email: string): Promise<MessageResponse> {
  const { data } = await api.post<MessageResponse>('/api/v1/auth/forgot-password', { email });
  return data;
}

/**
 * Submits a reset token + new password. Throws on invalid/expired token
 * or weak password (server enforces the policy).
 */
export async function resetPassword(token: string, newPassword: string): Promise<MessageResponse> {
  const { data } = await api.post<MessageResponse>('/api/v1/auth/reset-password', {
    token,
    new_password: newPassword,
  });
  return data;
}
